// Logistics pages (removals / receptions / deliveries): lot scanner, tag
// selects, and handing the photos Uppy is holding to the form's own file input
// before Turbo submits it.
//
// No submission rule lives here any more. Which action was submitted travels in
// the button's name/value, everything the operator gets wrong is caught by
// Logistics::ActionForm and rendered server-side, and the redirect afterwards is
// the controller's.
import ApplicationController from './application_controller'
import SlimSelect from 'slim-select'
import 'slim-select/styles'

import QrScanner from 'qr-scanner'

import QrScannerWorkerPath from 'qr-scanner/qr-scanner-worker.min.js?url'

QrScanner.WORKER_PATH = QrScannerWorkerPath

// Above this a photo is recompressed before upload (mirrors the shared
// resize-uploaded-files rule).
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024

export default class extends ApplicationController {
  static targets = [
    'itemCheckbox', 'actionTag', 'incidentTag', 'uploadsInput',
    'video', 'close', 'openButton', 'openedSection'
  ]

  connect () {
    if (document.getElementById('action_tags')) {
      this.actionSelect = new SlimSelect({ select: this.actionTagTarget })
    }
    if (document.getElementById('incident_tags')) {
      this.statusSelect = new SlimSelect({ select: this.incidentTagTarget })
    }

    if (document.getElementById('scan-panel')) {
      this.qrScanner = new QrScanner(this.videoTarget, result => this.scannedItem(result))
      this.qrScanner.start()
    }

    this.orderItemsIds = this.element.dataset.orderItemsIds.split(',')
    this.uploadsAttached = false
  }

  // SCANNER METHOD ______________________________________________________________________

  toggleScanPanel () {
    this.qrScanner ? this.hideScanPanel() : this.showScanPanel()
  }

  showScanPanel () {
    this.qrScanner = new QrScanner(this.videoTarget, result => this.scannedItem(result))
    this.qrScanner.start()
    this.openButtonTarget.style.display = 'none'
    this.openedSectionTarget.style.display = 'block'
  }

  hideScanPanel () {
    this.qrScanner.stop()
    this.qrScanner = null
    this.lastResult = null
    this.openButtonTarget.style.display = 'block'
    this.openedSectionTarget.style.display = 'none'
  }

  scannedItem (result) {
    if (this.lastResult === result) {
      return
    }
    this.lastResult = result

    try {
      const parsedResult = JSON.parse(result)
      const itemId = Number(parsedResult.id).toString()

      if (parsedResult.type !== 'item') {
        this.errorFeedback()
        return
      }
      if (this.orderItemsIds.indexOf(itemId) === -1) {
        this.errorFeedback()
        return
      }

      this.selectItem(itemId)
      this.successFeedback()
    } catch (e) {
      this.errorFeedback()
    }
  }

  // Ticking the checkbox is all there is to do: the row is a label, so the
  // selected state is pure CSS, and the id rides the form like a hand-ticked lot.
  selectItem (itemId) {
    const checkbox = this.itemCheckboxTargets
      .find(element => element.id.split('item_')[1] === itemId)

    if (checkbox) {
      checkbox.checked = true
    }
  }

  successFeedback () {
    this.beep('success')
    this.flash('green')
  }

  errorFeedback () {
    this.beep('error')
    this.flash('red')
  }

  flash (color) {
    const flashElement = document.getElementById('flash')
    const colorFlashClass = color + '-flash'

    flashElement.classList.add(colorFlashClass)
    flashElement.style.display = 'block'
    setTimeout(() => {
      flashElement.style.display = 'none'
      flashElement.classList.remove(colorFlashClass)
    }, 2000)
  }

  beep (kind) {
    const scanAudioFeedback = document.querySelector(`#scan-panel audio.${kind}`)

    scanAudioFeedback.load()
    setTimeout(() => {
      scanAudioFeedback.play()
    }, 50)
  }

  // SUBMISSION METHOD ____________________________________________________________________

  // Uppy holds the picked photos outside the form and they may need
  // recompressing, both of which are async — so the first submit is held back
  // just long enough to move them into the file input, then handed straight back
  // to Turbo.
  async submit (event) {
    if (this.uploadsAttached) {
      return
    }

    event.preventDefault()
    const form = event.target
    const submitter = event.submitter

    await this.attachUploads()
    this.uploadsAttached = true
    form.requestSubmit(submitter)
  }

  // A rejected submit leaves the page in place, so the next one has to pick up
  // whatever the operator added in the meantime.
  submitEnded () {
    this.uploadsAttached = false
  }

  // Both panes of the form post the same `uploads[]` input, so every picked
  // photo travels through a single one of them.
  async attachUploads () {
    this.uploadsInputTargets.forEach((input) => { input.value = '' })

    const files = await this.preparedUploads()
    if (files.length === 0 || this.uploadsInputTargets.length === 0) {
      return
    }

    const transfer = new DataTransfer()
    files.forEach(file => transfer.items.add(file))
    this.uploadsInputTargets[0].files = transfer.files
  }

  preparedUploads () {
    const uploads = this.utilities('upload-picture')
      .map(util => util.retrievePictures())
      .flat()

    return Promise.all(uploads.map(file => this.resizedIfNeeded(file)))
  }

  async resizedIfNeeded (file) {
    if (file.size <= MAX_UPLOAD_SIZE) {
      return file
    }

    const resized = await this.resizePhotos({ file, maxSize: 2000 })

    return this.blobToFile(resized, file.name)
  }

  blobToFile (blob, filename) {
    return this.utility('resize-uploaded-files').blobToFile(blob, filename)
  }

  resizePhotos (settings) {
    return this.utility('resize-uploaded-files').resizePhotos(settings)
  }

  utility (identifier) {
    return this.utilities(identifier)[0]
  }

  utilities (identifier) {
    const elements = document.querySelectorAll(`[data-controller="${identifier}"]`)

    return Array.from(elements)
                .map(el => this.application.getControllerForElementAndIdentifier(el, identifier))
  }
}
